import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Check } from "lucide-react";

export default function Course() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const location = useLocation();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    api.get(`/modules?lang=${lang}`).then((r) => setModules(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, [lang]);

  useEffect(() => {
    if (location.hash && modules.length) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [location.hash, modules]);

  const completed = new Set(progress?.completed_lessons || []);

  return (
    <div className="min-h-screen bg-void">
      <Navbar />
      <main className="pt-28 pb-16 max-w-4xl mx-auto px-6 md:px-12" data-testid="course-page">
        <p className="text-gold text-sm tracking-[0.3em] uppercase mb-3">{t("course.sub")}</p>
        <h1 className="font-serif text-5xl md:text-6xl text-ink mb-16">{t("course.title")}</h1>

        <div className="space-y-16">
          {modules.map((m, i) => (
            <section key={m.module_id} id={m.module_id} className="scroll-mt-28" data-testid={`course-module-${m.module_id}`}>
              <div className="flex items-baseline gap-4 mb-6">
                <span className="font-mono text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-serif text-3xl text-ink">{m.title}</h2>
              </div>
              <p className="text-ink-muted mb-6 pl-10">{m.description}</p>
              <ul className="pl-10 divide-y divide-ink/5 border-t border-ink/5">
                {m.lessons.map((l) => {
                  const done = completed.has(l.lesson_id);
                  return (
                    <li key={l.lesson_id}>
                      <Link to={`/lesson/${l.lesson_id}`}
                        className="group flex items-center justify-between py-4 hover:pl-2 transition-all"
                        data-testid={`course-lesson-${l.lesson_id}`}>
                        <span className={`text-lg transition-colors ${done ? "text-ink-soft" : "text-ink group-hover:text-gold"}`}>
                          <span className="font-mono text-xs text-ink-muted mr-4">{String(l.order).padStart(2, "0")}</span>
                          {l.title}
                        </span>
                        {done && <Check size={18} strokeWidth={1.5} className="text-gold" />}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}
